import { Button, DeleteIcon, Select, SelectOptionType } from '@bp/ui-components';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useCreateNumbersSelectValues } from '../../../../hooks/useCreateNumbersSelectValues';
import { usePriorityOptions } from '../../../../hooks/usePriorityOptions';
import styles from '../../Constraints.module.scss';
import { ConstraintsFormInitialValuesType } from '../../types';

type Field = 'maxBuildingChangesPerDay' | 'maxBuildingChangesPerWeek';

type BuildingChangesConstraintItemProps = {
  /** Which cap this row edits (per day or per week). */
  field: Field;
  /** i18n key for the row title. */
  labelKey: string;
  onRemove: () => void;
};

/**
 * „Max. Gebäudewechsel pro Tag/Woche" (class-condition-0007/-0008): a clearable number cap + priority.
 * Generic over the two caps — mirrors {@link FreePeriodConstraintItem}. `0` is a valid cap (no change allowed).
 */
export const BuildingChangesConstraintItem = ({ field, labelKey, onRemove }: BuildingChangesConstraintItemProps) => {
  const { t } = useTranslation();
  const { setFieldValue, setFieldTouched, values } = useFormikContext<ConstraintsFormInitialValuesType>();
  const priorityField = `${field}Priority` as const;

  const options = useCreateNumbersSelectValues();
  const priorityOptions = usePriorityOptions();

  const numberValue = values[field] as number | undefined;
  const priorityValue = values[priorityField] as string | undefined;

  const handleRemove = () => {
    setFieldValue(field, null);
    setFieldTouched(field, false);
    setFieldTouched(priorityField, false);
    setFieldValue(priorityField, null);
    onRemove();
  };

  return (
    <>
      <div className={styles['title']}>{t(labelKey)}</div>
      <div className={styles['row-wrapper']}>
        <div className='tks__row row-xs-11 mr-5'>
          <div className='tks__col col-xs-12'>
            <Select
              options={options}
              onChange={(value) => {
                const opt = value as SingleValue<SelectOptionType>;
                setFieldTouched(field, true);
                setFieldValue(field, opt?.value);
              }}
              value={numberValue !== undefined ? options[numberValue] : null}
              label={t('conditions.buildingChanges.count') as string}
              name={field}
            />
          </div>
        </div>
        <div className='tks__col col-xs-1'>
          <Select
            className={classNames(
              styles['priority-icon-select'],
              styles[`icon-${(priorityValue ?? '').toLowerCase()}`],
            )}
            options={priorityOptions}
            value={priorityOptions.find((option) => option.value === priorityValue)}
            onChange={(value) => {
              const opt = value as SingleValue<SelectOptionType>;
              setFieldTouched(priorityField, true);
              setFieldValue(priorityField, opt?.value);
            }}
            label={t('common.priority.full') as string}
            name={priorityField}
            fitMenuToOptions
            showIconOnly
          />
        </div>
        <div className={styles['button-wrapper']}>
          <Button name='remove' hierarchy='secondary' onClick={handleRemove} icon={<DeleteIcon />} />
        </div>
      </div>
    </>
  );
};
